/**
 * @render react
 * @name Select
 */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose } from 'recompose';
import ReactSelect from 'react-select';

import { withAmp } from 'Components/protons/withAmp';
import { withPolaris } from 'Components/protons/Polaris';

import renderAmp from './amp';

import './_style.scss';

/**
 * Select component
 *
 * @param {(string|number)} id - Define an id prefix for the select components e.g. {your-id}-value
 * @param {string} name - Name of the HTML Input (optional - without this, no input will be rendered)
 * @param {array} options - Options of the select. Objects within the array must contain the label and value keys
 * @param {Object} selectedOption - set the initial value of the control
 * @param {Function} onChange - Handle change events on the select
 * @param {Function} onBlur - Handle blur events on the control
 * @param {boolean} loadStyles - Whether to apply the class names for styling.
 * @param {boolean} isDisabled - disabled the control.
 * @param {Object} extraClassNames - Extra class names to be applied to the component
 * @param {Object} context - Extra information coming from Polaris provider
 * @param {Object} styles - Style modifier methods. Optional
 * @param {Object} placeholder - Placeholder text
 * @param {Object} reactSelectProps - pass all other props supported by third party component to the component
 * @see For more props option supported by React Select please visit {@link https://react-select.com/props|React Select API}
 */

class Select extends PureComponent {
  /**
   * Renders the amp version of the component.
   */
  amp() {
    return renderAmp.bind(this)();
  }

  render() {
    const {
      id, name, options, selectedOption, onChange, onBlur, placeholder, loadStyles, isDisabled, extraClassNames, context: { cssPrefix }, styles, reactSelectProps,
    } = this.props;
    return (
      <>
        <label htmlFor={id} className={`${cssPrefix}__select--label`}>{placeholder}</label>
        <ReactSelect
          instanceId={id}
          name={name}
          options={options}
          defaultValue={selectedOption}
          isDisabled={isDisabled}
          onChange={onChange}
          onBlur={onBlur}
          className={classNames({
            [`${cssPrefix}__select`]: loadStyles,
            ...extraClassNames,
          })}
          classNamePrefix={cssPrefix}
          placeholder={placeholder}
          styles={styles}
          {...reactSelectProps}
        />
      </>
    );
  }
}

/**
 * Valid props
 */
Select.propTypes = {
  id: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  name: PropTypes.string,
  options: PropTypes.instanceOf(Array),
  selectedOption: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  })),
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  extraClassNames: PropTypes.shape(),
  context: PropTypes.shape(),
  classNamePrefix: PropTypes.string,
  styles: PropTypes.shape(),
  reactSelectProps: PropTypes.shape(),
  loadStyles: PropTypes.bool,
  isDisabled: PropTypes.bool,
};

/**
 * Default props
 */
Select.defaultProps = {
  name: '',
  options: [],
  selectedOption: [],
  onBlur: null,
  onChange: null,
  loadStyles: true,
  extraClassNames: {},
  context: {},
  classNamePrefix: '',
  styles: {},
  reactSelectProps: {},
  placeholder: 'Select',
  isDisabled: false,
};

export default compose(withPolaris, withAmp)(Select);
